import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import { Button, Typography, useMediaQuery } from '@material-ui/core';
import { createElement, FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import ModificationRequestEditing from '../../../../entity/ModificationRequestEditing';
import UploadingFile from '../../../../entity/UploadingFile';
import {
  DeliverableForCraetionDetailFragment,
  Issue,
  IssueComment,
  RequestItem,
  RequestState,
} from '../../../../generated/graphql';
import theme from '../../../../theme';
import hmsToSeconds from '../../../../utility/hmsToSeconds';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import AspectRatioFixed from '../../primitive/AspectRatioFixed';
import LfToBr from '../../primitive/LfToBr';
import AvatarAndName from '../AvatarAndName';
import FileList, { FileListItem } from '../FileList';
import MediaPlayer from '../MediaPlayer';
import _CreateIssueDetailForm from './CreateIssueDetailForm';
import _IssueDetail from './IssueDetail';

interface Props {
  item: Pick<RequestItem, 'metadata' | 'assetIds' | 'typeName' | 'body' | 'id'> & {
    sender: { name?: string; profileImageUrl?: string };
    issue?:
      | (Pick<Issue, 'id' | 'resolved'> & {
          comments: Array<
            Pick<IssueComment, 'assetIds' | 'createdAt' | 'body'> & {
              user: { name?: string; profileImageUrl?: string };
            }
          >;
        })
      | null;
  };
  modificationRequestExpired: boolean;
  modificationRequestState: RequestState;
  modificationRequestEditing: ModificationRequestEditing;
  bucketId: string | null;
  deliverable?: DeliverableForCraetionDetailFragment | null;
  files: Array<FileReference>;
  index: number;
  onRemoveAllAssetFile: () => void;
  onUploadIssueAssetFiles: (files: UploadingFile[], bucketId: string, itemId: string) => void;
  onUploadIssueCommentAssetFiles: (files: UploadingFile[], deliverableId: string, issueId: string) => void;
  onRemoveAssetIssueFile: (bucketId: string, fileId: string, issueId: string) => void;
  onRemoveAssetIssueCommentFile: (bucketId: string, fileId: string, issueId: string) => void;
  onSubmitModificationRequestIssue: (
    modificationRequestItemId: string,
    inputComment: string,
    bucketId: string | null,
    files: Array<FileReference>,
  ) => Promise<void>;
  onSubmitModificationRequestIssueComment: (issueId: string, comment: string) => void;
  onRequestResolveModificationRequestIssue: (id: string) => void;
  onRequestReopenModificationRequestIssue: (id: string) => void;
  className?: string;
}

const Item: FC<Props> = ({
  item,
  modificationRequestExpired,
  modificationRequestState,
  modificationRequestEditing,
  bucketId,
  deliverable,
  files,
  index,
  onUploadIssueAssetFiles,
  onUploadIssueCommentAssetFiles,
  onRemoveAssetIssueFile,
  onRemoveAssetIssueCommentFile,
  onSubmitModificationRequestIssue,
  onSubmitModificationRequestIssueComment,
  onRequestResolveModificationRequestIssue,
  onRequestReopenModificationRequestIssue,
  className,
}) => {
  const duration = item.metadata.duration;
  const fileItems = files.filter((f) => item.assetIds.includes(f.id));
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isModificationRequestItemFormOpen, modificationRequestItemFormOpen] = useState(false);

  useEffect(() => {
    if (videoRef && videoRef.current && duration) {
      const videoEl = videoRef.current;
      const canPlayCallback = () => {
        if (videoEl.paused) {
          videoEl.currentTime = hmsToSeconds(duration);
        }
      };
      videoEl.addEventListener('canplay', () => canPlayCallback(), {
        once: true,
      });
      return videoEl.removeEventListener('canplay', canPlayCallback);
    }
  }, [duration]);

  const createIsue = modificationRequestEditing.createIssues.find((issue) => issue.itemId === item.id)!;

  const removeAssetIssueFile = () => {
    createIsue.assetIds.forEach((assetId) => {
      onRemoveAssetIssueFile(bucketId!, assetId, item.id);
    });
  };

  const AddItemIssueButton =
    deliverable && (modificationRequestState === 'submitted' || modificationRequestState === 'responded') ? (
      <Button variant="contained" color="primary" onClick={() => modificationRequestItemFormOpen(true)}>
        <FormattedMessage id="modification_request.creating_card.add_item" />
      </Button>
    ) : null;

  const isPC = useMediaQuery(`(min-width:${theme.breakpoints.values['lg']}px)`);

  return (
    <Root className={className}>
      {deliverable ? (
        <DeliverableScreen>
          <VideoOrImage aspectRatio={[4, 3]}>
            <MediaPlayer
              mimeType={deliverable.creative.file.mimeType}
              url={deliverable.creative.file.url}
              videoRef={videoRef}
            />
          </VideoOrImage>
        </DeliverableScreen>
      ) : null}

      <Content>
        <Titles>
          <Title variant="subtitle1">
            <FormattedMessage id="modification_request.detail_card.modification_request_item.title" />
            {(index + 1).toString()}
          </Title>
          {duration &&
          deliverable &&
          (deliverable.creative.file.mimeType.startsWith('video/') ||
            deliverable.creative.file.mimeType.startsWith('audio/')) ? (
            <Duration
              color="primary"
              variant="body1"
              onClick={() => {
                if (videoRef && videoRef.current) {
                  videoRef.current.currentTime = hmsToSeconds(duration);
                }
              }}
            >
              {duration}
            </Duration>
          ) : null}
          <User user={item.sender} size={24} />
        </Titles>

        {item.typeName ? <Type variant="subtitle1">{item.typeName}</Type> : null}

        <Comment variant="subtitle1" component="p">
          <LfToBr text={item.body} />
        </Comment>

        {fileItems ? (
          <FileList>
            {[
              ...fileItems.map((file) => (
                <FileListItem
                  isPC={isPC}
                  file={file}
                  key={`modification-request-file-${file.id}`}
                  isVisibleSecondary={false}
                />
              )),
            ]}
          </FileList>
        ) : null}

        {item.issue && (createIsue || modificationRequestEditing.createIssueComments.length > 0) ? (
          <IssueDetail
            deliverable={deliverable}
            bucketId={bucketId}
            issue={item.issue}
            files={files}
            createIssue={createIsue}
            createIssueComment={
              modificationRequestEditing.createIssueComments.find((comment) => comment.issueId === item.issue!.id)!
            }
            modificationRequestApproved={modificationRequestState === 'approved'}
            modificationRequestExpired={modificationRequestExpired}
            onRemoveAssetIssueCommentFile={onRemoveAssetIssueCommentFile}
            onUploadIssueCommentAssetFiles={onUploadIssueCommentAssetFiles}
            onSubmitModificationRequestIssueComment={onSubmitModificationRequestIssueComment}
            onRequestResolveModificationRequestIssue={onRequestResolveModificationRequestIssue}
            onRequestReopenModificationRequestIssue={onRequestReopenModificationRequestIssue}
          />
        ) : item.id && isModificationRequestItemFormOpen ? (
          <CreateIssueDetailForm
            itemId={item.id}
            modificationRequestState={modificationRequestState}
            bucketId={bucketId}
            files={files}
            createIssue={createIsue}
            onRemoveAssetIssueFile={onRemoveAssetIssueFile}
            onUploadIssueAssetFiles={onUploadIssueAssetFiles}
            onCancel={() => {
              modificationRequestItemFormOpen(false);
              removeAssetIssueFile();
            }}
            onSubmit={async (
              itemId: string,
              inputComment: string,
              bucketId: string | null,
              files: Array<FileReference>,
            ) => {
              await onSubmitModificationRequestIssue(itemId, inputComment, bucketId, files).then(() => {
                modificationRequestItemFormOpen(false);
              });
            }}
          />
        ) : (
          AddItemIssueButton
        )}
      </Content>
    </Root>
  );
};

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['35%', 'minmax(0, 680px)'],
    rows: ['auto'],
    areas: [['deliverable', 'content']],
  }),
  {
    paddingTop: SPACING_REGULAR,
    borderTop: `1px ${theme.palette.grey['200']} solid`,
  },
);

const DeliverableScreen = styled('div')(gridLayoutItem('deliverable', { alignSelf: 'flex-start' }), {
  marginRight: SPACING_REGULAR,
});

const VideoOrImage = styled(AspectRatioFixed)({
  marginBottom: SPACING_REGULAR,
});

const Content = styled('div')(gridLayoutItem('content', { alignSelf: 'flex-start' }));

const Titles = styled('div')(flexibleLayoutContainer({ alignItems: 'center' }), {
  marginBottom: SPACING_REGULAR,
});

const Title = styled(Typography)({ fontWeight: 700, minWidth: 84 });

const Duration = styled(Typography)({
  marginLeft: SPACING_REGULAR,
  cursor: 'pointer',
});

const User = styled(AvatarAndName)({
  marginLeft: SPACING_REGULAR,
  marginBottom: SPACING_UNIT / 2,
});

const Type = styled(Typography)({
  marginBottom: SPACING_UNIT,
});

const Comment = (styled(Typography)({
  wordBreak: 'break-word',
  hyphens: 'auto',
}) as unknown) as typeof Typography;

const IssueDetail = styled(_IssueDetail)({
  marginTop: SPACING_REGULAR,
  padding: SPACING_UNIT * 3,
  border: `1px ${theme.palette.grey['200']} solid`,
});

const CreateIssueDetailForm = styled(_CreateIssueDetailForm)({
  marginTop: SPACING_REGULAR,
  padding: SPACING_UNIT * 3,
  border: `1px ${theme.palette.grey['200']} solid`,
});

export default Item;
